<script setup lang="ts">
/* eslint @intlify/vue-i18n/no-v-html: 'off' */
import { onMounted, computed } from 'vue';
import { AkIcon, AkParagraph } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { CartRepeatOffer, RetailerShippingFeesMessageTypeName } from '@monolith/legacy/modules/cart-2/types';
import { formatPrice } from '@core/utilities/price.i18n';

import Analytics from '@monolith/legacy/services/analytics';
import { UserDisplayEvent, UserDisplayEventAction } from '@monolith/legacy/services/analytics/events/user-display.event';
import { formatDateInTimeZone } from '@monolith/legacy/services/date-locale';

const props = defineProps<{
  repeatOffer?: CartRepeatOffer;
  type: RetailerShippingFeesMessageTypeName;
  brandId: string;
}>();

function trackRepeatOffer() {
  Analytics.track(
    new UserDisplayEvent({
      component: 'cart_repeat_offer_brand_to_unlock',
      action: UserDisplayEventAction.DISPLAY,
      value: props.brandId,
    })
  );
}

const hasFreeShippingContent = computed(() => {
  if (props.type !== 'freeShipping') {
    return false;
  }
  return (props.repeatOffer?.activeDiscount?.percentage ?? 0) > 0 || (props.repeatOffer?.percentage ?? 0) > 0;
});

const shouldShowBanners = computed(() => {
  return hasFreeShippingContent.value;
});

onMounted(() => {
  if (!!props.repeatOffer && props.repeatOffer.percentage > 0 && props.type !== 'freeShipping') {
    trackRepeatOffer();
  }
});

const { t } = useI18n();
</script>

<template>
  <div
    v-if="shouldShowBanners"
    class="shipping-fees-banner-container"
    data-testid="shippingFeesBanner"
  >
    <div
      v-if="hasFreeShippingContent"
      class="shipping-fees-banner"
      data-testid="freeShippingBanner"
    >
      <AkParagraph
        class="shipping-fees-banner__content shipping-fees-banner__content--guttered"
        size="sm"
      >
        <template v-if="repeatOffer?.activeDiscount?.percentage > 0">
          <i18n-t
            keypath="retailer.cart.shippingFees.repeatOffer.unlocked.lbl"
            data-testid="repeatOfferUnlockedLabel"
          >
            <template #logoRepeat>
              <AkIcon
                symbol="repeat-offer"
                class="shipping-fees-banner__icon shipping-fees-banner__icon--aks"
                size="md"
              />
            </template>
            <template #percentage>
              <strong>{{
                t('retailer.cart.shippingFees.repeatOffer.unlocked.discount', {
                  percentage: repeatOffer.activeDiscount.percentage,
                })
              }}</strong>
            </template>
            <template #date>
              <strong>{{ formatDateInTimeZone(repeatOffer.activeDiscount.endDate, 'do MMMM') }}</strong>
            </template>
          </i18n-t>
        </template>

        <template v-else-if="repeatOffer?.percentage > 0">
          <i18n-t
            keypath="retailer.cart.shippingFees.freeShippingWithRepeatOffer.lbl"
            data-testid="repeatOfferLabel"
          >
            <template #logoRepeat>
              <AkIcon
                symbol="repeat-offer"
                class="shipping-fees-banner__icon shipping-fees-banner__icon--aks"
                size="md"
              />
            </template>
            <template #amountLeftToReachThreshold>
              <strong>{{ formatPrice(repeatOffer.amountLeftToReachThreshold) }}</strong>
            </template>
            <template #percentage>
              <strong>{{ t('retailer.cart.shippingFees.repeatOffers.discount', { percentage: repeatOffer.percentage }) }}</strong>
            </template>
          </i18n-t>
        </template>
      </AkParagraph>
    </div>
  </div>
</template>

<style lang="scss">
.shipping-fees-banner-container {
  @apply ds-flex ds-h-full;
}
.shipping-fees-banner {
  @apply ds-flex ds-flex-row ds-gap-2;
  &__icon {
    @apply ds-text-info-700 ds-rounded-lg;
    &--aks {
      @apply ds-bg-accent-alt ds-text-primary ds-flex ds-items-center ds-justify-center;
      width: 21px;
      height: 21px;
    }
  }
  &__bold {
    @apply ds-font-semibold;
  }
  &__content {
    @apply ds-inline-flex ds-flex-wrap ds-items-center;
    &--guttered {
      @apply ds-gap-1;
    }
  }
  strong {
    @apply ds-font-bold;
  }
}

.non-eligible-fee-banner__link {
  @apply ds-underline ds-px-1;
}

.first-order__cart {
  @apply ds-flex;
}
</style>
